/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorological Institute (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { scaleThreshold } from 'd3';

const ramp = (range: string[]) => {
  const num = range.length;
  return (t: number): string => {
    return range[Math.max(0, Math.min(num - 1, Math.floor(t * num)))];
  };
};

const bioRadColors = [
  '#FFFFFF',
  '#FFFFFB',
  '#FFFFF8',
  '#FFFFF5',
  '#FFFFF2',
  '#FFFFEE',
  '#FFFFEB',
  '#FFFFE8',
  '#FFFFE5',
  '#FFFFE1',
  '#FFFFDE',
  '#FFFFDB',
  '#FFFFD8',
  '#FFFFD5',
  '#FFFFD1',
  '#FFFFCB',
  '#FFFFC8',
  '#FFFFC4',
  '#FFFFC1',
  '#FFFFBE',
  '#FFFFBB',
  '#FFFFB7',
  '#FFFFB4',
  '#FFFFB1',
  '#FFFFAE',
  '#FFFFAB',
  '#FFFFA7',
  '#FFFFA4',
  '#FFFFA1',
  '#FFFF9A',
  '#FFFF97',
  '#FFFF94',
  '#FFFF91',
  '#FFFF8E',
  '#FFFF8A',
  '#FFFF87',
  '#FFFF84',
  '#FFFF81',
  '#FFFF7D',
  '#FFFF7A',
  '#FFFF77',
  '#FFFF74',
  '#FFFF70',
  '#FFFF6A',
  '#FFFF67',
  '#FFFF64',
  '#FFFF60',
  '#FFFF5D',
  '#FFFF5A',
  '#FFFF57',
  '#FFFF53',
  '#FFFF50',
  '#FFFF4D',
  '#FFFF4A',
  '#FFFF47',
  '#FFFF43',
  '#FFFF40',
  '#FFFF3A',
  '#FFFF36',
  '#FAFF33',
  '#F5FF30',
  '#F0F82D',
  '#EBF229',
  '#E6EC26',
  '#E1E623',
  '#DDE020',
  '#D8DA1D',
  '#D3D419',
  '#CECD16',
  '#C9C713',
  '#C4C110',
  '#BBB509',
  '#B6AF06',
  '#B1A903',
  '#ACA300',
  '#A7A300',
  '#A3A300',
  '#AAA300',
  '#B2A300',
  '#BAA300',
  '#C1A300',
  '#C9A300',
  '#D1A300',
  '#D8A300',
  '#E0A300',
  '#EFA300',
  '#F7A300',
  '#FFA300',
  '#FFA300',
  '#FFA300',
  '#FFA300',
  '#FFA305',
  '#FFA30B',
  '#FFA311',
  '#FFA317',
  '#FFA31D',
  '#FFA323',
  '#FFA329',
  '#FFA32E',
  '#FFA33A',
  '#FFA340',
  '#FFA346',
  '#FFA34C',
  '#FFA352',
  '#FF9D4D',
  '#FF9848',
  '#FF9343',
  '#FF8E3E',
  '#FF8939',
  '#FF8435',
  '#FF7F30',
  '#FF7A2B',
  '#FF7526',
  '#FF6A1C',
  '#FF6518',
  '#FF6013',
  '#FF5B0E',
  '#FF5609',
  '#FF5104',
  '#FF4C00',
  '#FF4700',
  '#FF4200',
  '#FF3D00',
  '#FF3800',
  '#FF3200',
  '#FF2D00',
  '#FF2800',
  '#FF1E00',
  '#FF1900',
  '#FF1400',
  '#FF0F00',
  '#FF0A00',
  '#FF0500',
  '#FF0000',
  '#FF0000',
  '#FF0000',
  '#FF0000',
  '#F90000',
  '#F30000',
  '#EE0000',
  '#E80000',
  '#DD0000',
  '#D70000',
  '#D20000',
  '#CC0000',
  '#C60000',
  '#C10000',
  '#BB0000',
  '#B60000',
  '#B00000',
  '#AA0005',
  '#A5000A',
  '#9F0010',
  '#990015',
  '#94001B',
  '#890025',
  '#83002B',
  '#7D0030',
  '#780036',
  '#72003B',
  '#6D0041',
  '#670046',
  '#61004B',
  '#5C0051',
  '#560056',
  '#51005C',
  '#510061',
  '#510067',
  '#51006C',
  '#510077',
  '#51007C',
  '#510082',
  '#510087',
  '#51008D',
  '#510092',
  '#510097',
  '#51009D',
  '#5100A2',
  '#5100A8',
  '#5100AD',
  '#4C00B3',
  '#4700B8',
  '#4200BD',
  '#3900C8',
  '#3400CE',
  '#2F00D3',
  '#2A00D9',
  '#2600DE',
  '#2100E3',
  '#1C00E9',
  '#1700EE',
  '#1300F4',
  '#0E00F9',
  '#0900FF',
  '#0400F9',
  '#0000F4',
  '#0000EF',
  '#0000E4',
  '#0000DF',
  '#0000DA',
  '#0000D5',
  '#0000D0',
  '#0000CA',
  '#0000C5',
  '#0000C0',
  '#0000BB',
  '#0000B6',
  '#0000B0',
  '#0000AB',
  '#0000A6',
  '#0000A1',
  '#000096',
  '#000091',
  '#00008C',
  '#000087',
  '#000082',
  '#00007C',
  '#000077',
  '#000072',
  '#00006D',
  '#000068',
  '#000062',
  '#00005D',
  '#000058',
  '#000053',
  '#000048',
  '#000043',
  '#00003E',
  '#000039',
  '#000034',
  '#00002E',
  '#000029',
  '#000024',
  '#00001F',
  '#00001A',
  '#000014',
  '#00000F',
  '#00000A',
  '#000005',
  '#000000',
];
// bioRad scale for VID
export const interpolateBioRadColorScale = ramp(bioRadColors);

// birdTAM scale
export const birdTAMColorScale = scaleThreshold<number, string>()
  .domain([0, 1, 2, 3, 4, 5, 6, 7, 8, 9])
  .range([
    '#FFFFFF',
    '#FFFFFF',
    '#E6FFE6',
    '#CCFFCC',
    '#B2FFB2',
    '#99FF99',
    '#00FF00',
    '#FFFF00',
    '#FF8000',
    '#FF0000',
    '#333333',
  ]);
